"use client";

import Button from "@/component-library/components/buttons/button/Button";
import { TextField } from "@/component-library/components/user-input/text-field/TextField";
import { SearchableCountrySelect } from "@/component-library/components/user-input/country-select/SearchableCountrySelect";
import { PhoneInputComponent } from "@/component-library/components/user-input/phone-input/PhoneInput";
import { isMalformedPhoneNumber } from "@/component-library/components/user-input/phone-input/phoneNumberValidation";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { FormEntry } from "@/component-library/components/user-input/forms/form-entry/FormEntry";
import CustomDialog from "@/component-library/components/feedback/custom-dialog/CustomDialog";
import { FontAwesomeIcon } from "@/component-library/components/media/iconography/FontAwesomeIcon";
import { NetworkEndpoints } from "@/configs/NetworkEndpoints";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { formatCurrency } from "@/common/utilities/math/asCurrency";
import { clampBudget } from "@/common/utilities/money/budgetBounds";
import { minorUnitFactor } from "@/common/utilities/money/currencyPrecision";
import { useBudgetBounds } from "@/common/utilities/money/useBudgetBounds";
import { zoomBudgetBounds } from "@/features/office-price-benchmark/budgetAdvice";
import {
    capacityForSeats,
    MAX_SEATS,
    MIN_SEATS,
    seatsForCapacity,
} from "@/features/office-price-benchmark/officePriceBenchmark";
import { useOfficePriceBenchmarks } from "@/features/office-price-benchmark/useOfficePriceBenchmarks";
import { useLatestLeadInquiry } from "@/features/lead-inquiries/useLatestLeadInquiry";
import { reportAnalyticsEvent } from "@/features/analytics/application/reportAnalyticsEvent";
import { AnalyticsEventType } from "@/features/analytics/domain/entities/AnalyticsEventType";
import { useRecaptcha } from "@/features/recaptcha/useRecaptcha";
import { TOfficeType } from "@/features/filtering/filters/office-type-filter/officeTypeFilterQueryParam";
import {
    faBuilding,
    faCalendarAlt,
    faWallet,
    faComment,
    faCheckCircle,
    faMinus,
    faPlus,
    faUsers,
} from "@fortawesome/free-solid-svg-icons";
import React, { FC, useMemo, useState } from "react";
import { BudgetAdvice, BudgetSlider } from "./BudgetSlider";
import { getDefaultMoveInDate } from "./defaultMoveInDate";
import { DateField } from "./DateField";
import { ViewingDateField } from "./ViewingDateField";
import { LEAD_CONSENT_TEXT } from "./leadConsentText";
import {
    inquiryFormStyle,
    inquiryFormFieldGroupStyle,
    inquiryFormSuccessStyle,
    inquiryFormSuccessIconStyle,
    inquiryFormHeaderStyle,
    inquiryFormHeaderSubtitleStyle,
    inquiryFormDividerStyle,
    inquiryFormSectionTitleStyle,
    inquiryFormTextareaStyle,
    inquiryFormSliderStyle,
    inquiryFormSliderUnsetStyle,
    inquiryFormBudgetLabelStyle,
    inquiryFormBudgetUnsetHintStyle,
    inquiryFormActionsStyle,
    inquiryFormConsentStyle,
    inquiryFormErrorStyle,
    inquiryFormToggleGroupStyle,
    inquiryFormToggleItemStyle,
    inquiryFormSeatsStyle,
    inquiryFormSeatsLabelStyle,
    inquiryFormSeatsButtonStyle,
    inquiryFormSeatsCountStyle,
    inquiryFormColumnsStyle,
    inquiryFormColumnStyle,
    inquiryFormSecondColumnStyle,
} from "./PrivateOfficeInquiryForm.css";

interface PrivateOfficeInquiryFormProps {
    isOpen: boolean;
    /** Set when the user opened the form from a specific location's page. */
    locationId?: string;
    locationName?: string;
    currencyCode: string;
    /** The location's country. Reported with the lead analytics event, and used
     *  as the phone input's fallback country -- only when the enquirer's own
     *  country cannot be detected from their browser, since the number is
     *  theirs rather than the location's. Optional; omitted when the form is
     *  opened outside a location-page context. */
    countryCode?: string;
    /** Pre-selected capacity (set when opening from a host page where the user
     *  picked a capacity). Seeds the seat count, which stays adjustable. */
    desiredCapacity?: string;
    /**
     * When the inquiry is about a specific listed option, its id is recorded on
     * the inquiry and its name shown in the form. Only applies to the
     * location-based path; omitted for generic inquiries.
     */
    workspaceGroupId?: string;
    optionName?: string;
    /**
     * Whether the enquirer is after serviced or conventional space: the
     * selected option's type, or the office-type filter they had applied.
     * Recorded on the inquiry so Salesforce can set it on the Opportunity.
     * Omitted when unknown.
     */
    officeType?: TOfficeType | null;
    onClose: () => void;
}

const LEASE_DURATION_OPTIONS = [
    { value: "MONTHS_1_3", label: "1-3 months" },
    { value: "MONTHS_3_6", label: "3-6 months" },
    { value: "MONTHS_6_12", label: "6-12 months" },
    { value: "MONTHS_12_PLUS", label: "12+ months" },
];

// Validation messages for the fields the browser cannot check itself, keyed by
// field. An absent key means that field is fine.
type FieldValidationProblems = {
    country?: string;
    phone?: string;
    budget?: string;
};

// The keys a range input responds to. Used to tell a deliberate keyboard
// adjustment apart from Tab-ing through the form; see markBudgetChosen.
const BUDGET_SLIDER_KEYS = new Set([
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
    "PageUp",
    "PageDown",
]);

const PrivateOfficeInquiryForm: FC<PrivateOfficeInquiryFormProps> = ({
    isOpen,
    locationId,
    locationName,
    currencyCode,
    countryCode,
    desiredCapacity: initialDesiredCapacity,
    workspaceGroupId,
    optionName,
    officeType,
    onClose,
    // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
    const isLocationBased = !!locationId;

    const { isAuthenticated, user, getToken } = useAuth();
    const { data: latestInquiry, isLoading: isInquiryLoading } =
        useLatestLeadInquiry();
    const { executeRecaptcha } = useRecaptcha();

    const [phone, setPhone] = useState(user?.invoiceInformation?.phone ?? "");
    const [companyName, setCompanyName] = useState(
        user?.invoiceInformation?.companyName ?? ""
    );
    const [guestFirstName, setGuestFirstName] = useState("");
    const [guestLastName, setGuestLastName] = useState("");
    const [guestEmail, setGuestEmail] = useState("");
    // Only collected on the location-less form: without a location row there is
    // no country for the backend to derive, so the enquirer names it directly.
    // Holds an ISO 3166-1 alpha-2 code, or undefined until one is picked.
    const [desiredCountryCode, setDesiredCountryCode] = useState<
        string | undefined
    >(undefined);
    const [desiredLocationDescription, setDesiredLocationDescription] =
        useState("");
    // A seat count rather than a capacity range: the budget advice needs to
    // know how many seats the budget is for. The lead still records the range
    // the count falls in.
    const [seats, setSeats] = useState(() =>
        seatsForCapacity(initialDesiredCapacity)
    );
    const desiredCapacity = capacityForSeats(seats);

    // Override with latest inquiry data once it loads
    const [appliedInquiryPrefill, setAppliedInquiryPrefill] = useState(false);
    React.useEffect(() => {
        if (appliedInquiryPrefill || isInquiryLoading || !latestInquiry) return;
        setAppliedInquiryPrefill(true);
        if (latestInquiry.phone) setPhone(latestInquiry.phone);
        if (latestInquiry.companyName)
            setCompanyName(latestInquiry.companyName);
    }, [latestInquiry, isInquiryLoading, appliedInquiryPrefill]);
    const [desiredMoveInDate, setDesiredMoveInDate] = useState(
        getDefaultMoveInDate()
    );
    // Optional on every inquiry: an enquirer who already knows when they want
    // to visit says so here, and the backend flags the lead to operations as a
    // viewing request. Empty means they did not ask for one, so there is no
    // default -- a prefilled date would claim a slot nobody chose. Timezone-less
    // YYYY-MM-DDTHH:mm, treated as wall-clock time at the location.
    const [desiredViewingDate, setDesiredViewingDate] = useState("");
    const [desiredLeaseDuration, setDesiredLeaseDuration] =
        useState("MONTHS_6_12");
    // No default. A range input always reports a value, so an untouched slider
    // used to submit a budget the customer never picked -- whatever the bottom
    // of the range happened to be, which is now currency-dependent too.
    // `null` means "not chosen yet" and blocks submission.
    const [maxBudget, setMaxBudget] = useState<number | null>(null);
    // The slider's range in the quoted currency's minor units. Anchored in EUR
    // and scaled at the live rate, so the same real budget is expressible
    // whatever the location is priced in; see `budgetBounds`.
    const fullBudgetBounds = useBudgetBounds(currencyCode);
    // What offices of this size usually cost around the location, when that is
    // known. Conventional space has no ranges yet; an office type left open
    // ("All") is compared with serviced offices, which nearly all are.
    const benchmarks = useOfficePriceBenchmarks(
        locationId,
        officeType === "CONVENTIONAL" ? "CONVENTIONAL" : "SERVICED"
    );
    const benchmark =
        benchmarks?.currency === currencyCode
            ? benchmarks.capacities[desiredCapacity]
            : null;
    // With a benchmark the slider spans the part of the range that matters for
    // the team's size, stretched to keep a budget already chosen; see
    // zoomBudgetBounds. Memoised because the effect below re-clamps the budget
    // whenever the bounds change.
    const budgetBounds = useMemo(
        () =>
            benchmark
                ? zoomBudgetBounds(
                      fullBudgetBounds,
                      seats,
                      benchmark,
                      maxBudget
                  )
                : fullBudgetBounds,
        [fullBudgetBounds, seats, benchmark, maxBudget]
    );
    // The rate resolves a round trip after first paint, so a budget picked
    // against the fallback range has to be pulled into the real one -- the
    // input clamps an out-of-range value on its own but never says so, leaving
    // state holding a figure the thumb is no longer on. The same goes for the
    // range narrowing to a new seat count or once the benchmark arrives.
    React.useEffect(() => {
        setMaxBudget((current) =>
            current === null ? null : clampBudget(current, budgetBounds)
        );
    }, [budgetBounds]);
    const [comment, setComment] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Gates the per-field validation messages: hidden until the user has
    // actually tried to submit, so an untouched form is not covered in
    // complaints. See findValidationProblems below.
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

    // When the dialog is reopened after a previous successful submit, drop the
    // success view so the populated form is shown again (field values are kept
    // intentionally so the user can tweak and resubmit).
    const wasOpenRef = React.useRef(isOpen);
    React.useEffect(() => {
        if (isOpen && !wasOpenRef.current) {
            setHasAttemptedSubmit(false);
            if (isSubmitted) {
                setIsSubmitted(false);
                setError(null);
            }
        }
        wasOpenRef.current = isOpen;
    }, [isOpen, isSubmitted]);

    // Null until the enquirer has actually chosen a budget, so the form can
    // tell "not chosen yet" from "chosen the minimum".
    const budgetFormatted =
        maxBudget === null ? null : formatCurrency(maxBudget, currencyCode);

    // A range input fires no change event when an interaction leaves the value
    // where it already was -- grabbing the thumb at its resting position, or
    // pressing Left while at the minimum. Those are still deliberate choices,
    // so record them here instead of leaving the form stuck on "not chosen".
    const markBudgetChosen = (element: HTMLInputElement) => {
        setMaxBudget((current) => current ?? Number(element.value));
    };

    // A number missing its country code is silently reinterpreted by the input
    // -- a US 5734989801 becomes +57 34989801 -- so an unchecked lead reaches
    // the operator with a number that cannot be dialled. Block the submit on
    // it; the input explains itself to the user.
    const phoneIsMalformed = isMalformedPhoneNumber(phone);

    // Most fields carry a native `required`, so the browser blocks submission
    // and points at them on its own -- but only if the submit button is
    // enabled, which is why it no longer gates on completeness. These three
    // live in React state rather than in a native input (the country field
    // shows a name while the code is held separately, the slider always
    // reports a number, and the phone input is a third-party component), so
    // the browser cannot see them and they are checked here instead.
    const findValidationProblems = (): FieldValidationProblems => {
        const problems: FieldValidationProblems = {};
        if (!isLocationBased && !desiredCountryCode) {
            problems.country = "Please choose the country you're looking in.";
        }
        if (!phone) {
            problems.phone = "Please enter your phone number.";
        } else if (phoneIsMalformed) {
            problems.phone =
                "This number doesn't look valid. Check the country code and try again.";
        }
        if (maxBudget === null) {
            problems.budget = "Please choose your maximum monthly budget.";
        }
        return problems;
    };

    // Recomputed every render once the user has attempted a submit, so each
    // message clears the moment its field is fixed rather than only on the
    // next attempt.
    const validationProblems: FieldValidationProblems = hasAttemptedSubmit
        ? findValidationProblems()
        : {};

    // The per-field messages are the precise feedback, but the offending field
    // can be scrolled out of view in a long form. This sits next to the button,
    // where the user is looking when they click, so a blocked submit always
    // says something.
    const validationSummary =
        Object.keys(validationProblems).length > 0
            ? "Please complete the highlighted fields above."
            : null;

    const buildBaseBody = (): Record<string, unknown> => ({
        ...(isLocationBased
            ? {
                  locationId,
                  // An option only makes sense for a concrete location.
                  ...(workspaceGroupId ? { workspaceGroupId } : {}),
              }
            : {
                  desiredLocationDescription: desiredLocationDescription.trim(),
                  // The location-less lead carries no location, so this is the
                  // only country signal the operator gets.
                  ...(desiredCountryCode
                      ? { countryCode: desiredCountryCode }
                      : {}),
              }),
        currency: currencyCode,
        phone: formatPhoneNumberIntl(phone) || phone,
        companyName,
        desiredMoveInDate,
        // Omitted rather than sent empty: the backend reads its presence as
        // "this lead wants a viewing" and flags it to operations, so an empty
        // string would claim a slot the enquirer never asked for.
        ...(desiredViewingDate ? { desiredViewingDate } : {}),
        desiredCapacity,
        desiredLeaseDuration,
        maxBudget,
        comment: comment || undefined,
        ...(officeType ? { officeType } : {}),
    });

    type PreparedRequest =
        | {
              headers: Record<string, string>;
              body: Record<string, unknown>;
          }
        | { error: string };

    const prepareRequest = async (): Promise<PreparedRequest> => {
        const headers: Record<string, string> = {
            "Content-Type": "application/json",
        };
        const body = buildBaseBody();

        if (isAuthenticated) {
            const token = getToken();
            if (!token) {
                return {
                    error: "Authentication expired. Please log in again.",
                };
            }
            headers.Authorization = token;
            return { headers, body };
        }

        const captchaToken = await executeRecaptcha("private_office_inquiry");
        body.firstName = guestFirstName;
        body.lastName = guestLastName;
        body.email = guestEmail;
        if (captchaToken) body.captchaToken = captchaToken;
        return { headers, body };
    };

    // [inquiryId] is the id the backend assigned the inquiry this event
    // reports, sent as the event's transaction id. It is what makes the lead
    // countable once: an event replayed for the same inquiry -- a retried
    // send, a duplicated tag, the same conversion seen by both GA4 and Google
    // Ads -- carries the same id and is deduplicated against the first.
    const reportLeadAnalytics = (inquiryId?: string) => {
        void reportAnalyticsEvent({
            eventType: AnalyticsEventType.generateLeadPO,
            // "private-office-viewing" used to mean "came in through the
            // schedule-a-viewing button". The button is gone, but the segment
            // it measured -- a lead that wants to visit -- is now simply a
            // lead that filled the viewing field, so the label keeps its
            // meaning and the historic series stays comparable.
            label: desiredViewingDate
                ? "private-office-viewing"
                : isLocationBased
                  ? "private-office-inquiry"
                  : "private-office-inquiry-no-location",
            metadata: {
                ...(inquiryId ? { transaction_id: inquiryId } : {}),
                location_id: locationId ?? null,
                location_name: locationName ?? null,
                capacity: desiredCapacity,
                lease_duration: desiredLeaseDuration,
                currency: currencyCode,
                // The budget is in minor units, and a minor unit is not a
                // hundredth of the major one in every currency: dividing a
                // JPY budget by 100 reports a lead worth a hundredth of the
                // real one.
                value:
                    maxBudget === null
                        ? null
                        : maxBudget / minorUnitFactor(currencyCode),
                country: countryCode,
                user_data: {
                    email_address: isAuthenticated ? user?.email : guestEmail,
                },
            },
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Reaching here means the browser is happy with every natively-required
        // field, so only the state-backed ones are left to check.
        setHasAttemptedSubmit(true);
        // A new attempt supersedes whatever the last one said, so clear it
        // here rather than after the validation gate below -- otherwise a
        // stale "something went wrong" sits above the new field messages.
        setError(null);
        if (Object.keys(findValidationProblems()).length > 0) return;

        setIsSubmitting(true);
        setError(null);

        try {
            const prepared = await prepareRequest();
            if ("error" in prepared) {
                setError(prepared.error);
                return;
            }

            const response = await fetch(NetworkEndpoints.leadInquiry(), {
                method: "POST",
                headers: prepared.headers,
                body: JSON.stringify(prepared.body),
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || "Failed to submit inquiry.");
            }

            // The 201 body carries the created inquiry's id, which the lead
            // event reports as its transaction id. The inquiry itself is
            // already accepted at this point, so an unreadable body costs the
            // event its id rather than the user their submission.
            const created: { id?: string } | null = await response
                .json()
                .catch(() => null);

            reportLeadAnalytics(created?.id);
            setIsSubmitted(true);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "Something went wrong."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSubmitted) {
        return (
            <CustomDialog
                isOpen={isOpen}
                onCloseDialog={onClose}
                title=""
                showCloseIcon
                actions={[
                    <Button key="close" variant="secondary">
                        Close
                    </Button>,
                ]}
            >
                <div className={inquiryFormSuccessStyle}>
                    <div className={inquiryFormSuccessIconStyle}>
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <h3>
                        {desiredViewingDate
                            ? "Viewing request submitted!"
                            : "Inquiry submitted!"}
                    </h3>
                    <p>
                        {desiredViewingDate ? (
                            <>
                                Thank you for requesting a viewing at{" "}
                                <strong>{locationName}</strong>.
                                <br />
                                Our team will be in touch shortly to confirm
                                your viewing slot.
                            </>
                        ) : (
                            <>
                                {isLocationBased ? (
                                    <>
                                        Thank you for your interest in a private
                                        office at{" "}
                                        <strong>{locationName}</strong>.
                                    </>
                                ) : (
                                    <>
                                        Thank you for your interest in finding a
                                        private office through Wezoo.
                                    </>
                                )}
                                <br />
                                Our team will be in touch with you shortly.
                            </>
                        )}
                    </p>
                </div>
            </CustomDialog>
        );
    }

    return (
        <>
            <CustomDialog
                isOpen={isOpen}
                onCloseDialog={onClose}
                title=""
                actions={[]}
                maxWidthInRem={76}
                // On a phone the form is taller than the screen and its
                // Cancel button is at the very bottom; the X is the way out
                // that is always in view.
                showCloseIcon
                // The first field is a date input: see CustomDialog.
                initialFocus="content"
            >
                <form onSubmit={handleSubmit} className={inquiryFormStyle}>
                    <div className={inquiryFormHeaderStyle}>
                        <h2>Private office inquiry</h2>
                        <p className={inquiryFormHeaderSubtitleStyle}>
                            {isLocationBased
                                ? [optionName, locationName]
                                      .filter(Boolean)
                                      .join(" · ")
                                : "Tell us where you're looking for a private office. Wezoo has options all over" +
                                  " world, so don't hesitate to reach out!"}
                        </p>
                    </div>

                    <div className={inquiryFormDividerStyle} />

                    <div className={inquiryFormColumnsStyle}>
                        <div className={inquiryFormColumnStyle}>
                            {!isLocationBased && (
                                <>
                                    <h4
                                        className={inquiryFormSectionTitleStyle}
                                    >
                                        <FontAwesomeIcon icon={faBuilding} />{" "}
                                        Where are you looking?
                                    </h4>

                                    <div className={inquiryFormFieldGroupStyle}>
                                        <FormEntry
                                            label="Country"
                                            errorMessage={
                                                validationProblems.country
                                            }
                                        >
                                            <SearchableCountrySelect
                                                name="countryCode"
                                                value={desiredCountryCode}
                                                onValueChange={
                                                    setDesiredCountryCode
                                                }
                                                placeholder="Search for a country"
                                                descriptionForScreenReaders="Search for the country you are looking for space in"
                                            />
                                        </FormEntry>
                                    </div>

                                    <div className={inquiryFormFieldGroupStyle}>
                                        <FormEntry label="Desired location">
                                            <TextField
                                                name="desiredLocationDescription"
                                                value={
                                                    desiredLocationDescription
                                                }
                                                onChange={(value) =>
                                                    setDesiredLocationDescription(
                                                        value.slice(0, 500)
                                                    )
                                                }
                                                placeholder="City, neighbourhood, or building"
                                                required
                                            />
                                        </FormEntry>
                                    </div>

                                    <div className={inquiryFormDividerStyle} />
                                </>
                            )}

                            <h4 className={inquiryFormSectionTitleStyle}>
                                <FontAwesomeIcon icon={faCalendarAlt} />{" "}
                                Requirements
                            </h4>

                            <div className={inquiryFormFieldGroupStyle}>
                                <DateField
                                    label="Desired move-in date"
                                    type="date"
                                    value={desiredMoveInDate}
                                    onChange={setDesiredMoveInDate}
                                    required
                                />
                            </div>

                            <div className={inquiryFormFieldGroupStyle}>
                                <FormEntry label="Duration">
                                    <div
                                        className={inquiryFormToggleGroupStyle}
                                    >
                                        {LEASE_DURATION_OPTIONS.map((opt) => (
                                            <div
                                                key={opt.value}
                                                className={
                                                    inquiryFormToggleItemStyle
                                                }
                                                data-selected={
                                                    desiredLeaseDuration ===
                                                    opt.value
                                                }
                                                onClick={() =>
                                                    setDesiredLeaseDuration(
                                                        opt.value
                                                    )
                                                }
                                            >
                                                {opt.label}
                                            </div>
                                        ))}
                                    </div>
                                </FormEntry>
                            </div>

                            {/* Not a FormEntry: that wraps its field in a
                                <label>, which would hand every click on the
                                label text to the minus button. */}
                            <div className={inquiryFormFieldGroupStyle}>
                                <div
                                    className={inquiryFormSeatsStyle}
                                    role="group"
                                    aria-labelledby="inquiry-seats-label"
                                >
                                    <span
                                        id="inquiry-seats-label"
                                        className={inquiryFormSeatsLabelStyle}
                                    >
                                        <FontAwesomeIcon icon={faUsers} /> Seats
                                        needed
                                    </span>
                                    <button
                                        type="button"
                                        className={inquiryFormSeatsButtonStyle}
                                        aria-label="Fewer seats"
                                        disabled={seats <= MIN_SEATS}
                                        onClick={() =>
                                            setSeats((n) =>
                                                Math.max(MIN_SEATS, n - 1)
                                            )
                                        }
                                    >
                                        <FontAwesomeIcon icon={faMinus} />
                                    </button>
                                    <output
                                        className={inquiryFormSeatsCountStyle}
                                        aria-live="polite"
                                    >
                                        {seats}
                                    </output>
                                    <button
                                        type="button"
                                        className={inquiryFormSeatsButtonStyle}
                                        aria-label="More seats"
                                        disabled={seats >= MAX_SEATS}
                                        onClick={() =>
                                            setSeats((n) =>
                                                Math.min(MAX_SEATS, n + 1)
                                            )
                                        }
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                    </button>
                                </div>
                            </div>

                            <div className={inquiryFormFieldGroupStyle}>
                                <FormEntry
                                    label={
                                        <span
                                            className={
                                                inquiryFormBudgetLabelStyle
                                            }
                                        >
                                            <span>
                                                <FontAwesomeIcon
                                                    icon={faWallet}
                                                />{" "}
                                                Maximum monthly budget
                                            </span>
                                            {budgetFormatted ? (
                                                <strong>
                                                    {budgetFormatted}
                                                </strong>
                                            ) : (
                                                <span
                                                    className={
                                                        inquiryFormBudgetUnsetHintStyle
                                                    }
                                                >
                                                    Select your budget
                                                </span>
                                            )}
                                        </span>
                                    }
                                    errorMessage={validationProblems.budget}
                                >
                                    <BudgetSlider
                                        bounds={budgetBounds}
                                        budget={maxBudget}
                                        currency={currencyCode}
                                        seats={seats}
                                        benchmark={benchmark}
                                        input={
                                            <input
                                                type="range"
                                                min={budgetBounds.min}
                                                max={budgetBounds.max}
                                                step={budgetBounds.step}
                                                value={
                                                    maxBudget ??
                                                    budgetBounds.min
                                                }
                                                onChange={(e) =>
                                                    setMaxBudget(
                                                        Number(e.target.value)
                                                    )
                                                }
                                                onPointerDown={(e) =>
                                                    markBudgetChosen(
                                                        e.currentTarget
                                                    )
                                                }
                                                onKeyDown={(e) => {
                                                    if (
                                                        BUDGET_SLIDER_KEYS.has(
                                                            e.key
                                                        )
                                                    ) {
                                                        markBudgetChosen(
                                                            e.currentTarget
                                                        );
                                                    }
                                                }}
                                                aria-valuetext={
                                                    budgetFormatted ??
                                                    "No budget selected"
                                                }
                                                className={`${inquiryFormSliderStyle} ${
                                                    maxBudget === null
                                                        ? inquiryFormSliderUnsetStyle
                                                        : ""
                                                }`}
                                            />
                                        }
                                    />
                                </FormEntry>
                                <BudgetAdvice
                                    bounds={budgetBounds}
                                    budget={maxBudget}
                                    currency={currencyCode}
                                    seats={seats}
                                    benchmark={benchmark}
                                    onBudgetChange={setMaxBudget}
                                />
                            </div>
                        </div>

                        <div className={inquiryFormSecondColumnStyle}>
                            <h4 className={inquiryFormSectionTitleStyle}>
                                <FontAwesomeIcon icon={faBuilding} /> Your
                                contact details
                            </h4>

                            {!isAuthenticated && (
                                <>
                                    <div className={inquiryFormFieldGroupStyle}>
                                        <FormEntry label="First name">
                                            <TextField
                                                name="firstName"
                                                autoComplete="given-name"
                                                value={guestFirstName}
                                                onChange={setGuestFirstName}
                                                placeholder="Your first name"
                                                required
                                            />
                                        </FormEntry>
                                    </div>

                                    <div className={inquiryFormFieldGroupStyle}>
                                        <FormEntry label="Last name">
                                            <TextField
                                                name="lastName"
                                                autoComplete="family-name"
                                                value={guestLastName}
                                                onChange={setGuestLastName}
                                                placeholder="Your last name"
                                                required
                                            />
                                        </FormEntry>
                                    </div>
                                </>
                            )}

                            <div className={inquiryFormFieldGroupStyle}>
                                <FormEntry label="Contact email">
                                    {isAuthenticated && user?.email ? (
                                        <TextField
                                            name="email"
                                            value={user.email}
                                            disabled
                                        />
                                    ) : (
                                        <TextField
                                            name="email"
                                            type="email"
                                            autoComplete="email"
                                            value={guestEmail}
                                            onChange={setGuestEmail}
                                            placeholder="you@example.com"
                                            required
                                        />
                                    )}
                                </FormEntry>
                            </div>

                            <div className={inquiryFormFieldGroupStyle}>
                                <FormEntry
                                    label="Phone number"
                                    errorMessage={validationProblems.phone}
                                >
                                    <PhoneInputComponent
                                        value={phone}
                                        onChange={(value) =>
                                            setPhone(value ?? "")
                                        }
                                        defaultCountry={countryCode}
                                    />
                                </FormEntry>
                            </div>

                            <div className={inquiryFormFieldGroupStyle}>
                                <FormEntry label="Company name">
                                    <TextField
                                        name="companyName"
                                        autoComplete="organization"
                                        value={companyName}
                                        onChange={setCompanyName}
                                        placeholder="Your company name"
                                        required
                                        prefixIcon={
                                            <FontAwesomeIcon
                                                icon={faBuilding}
                                            />
                                        }
                                    />
                                </FormEntry>
                            </div>

                            {/* Only where there is a building to visit. On the
                                location-less form the enquirer is describing a
                                city, not a place, so a viewing time would be a
                                slot against nothing. */}
                            {isLocationBased && (
                                <div className={inquiryFormFieldGroupStyle}>
                                    <ViewingDateField
                                        value={desiredViewingDate}
                                        onChange={setDesiredViewingDate}
                                    />
                                </div>
                            )}

                            <div className={inquiryFormFieldGroupStyle}>
                                <FormEntry
                                    label={
                                        <>
                                            <FontAwesomeIcon icon={faComment} />{" "}
                                            Comment{" "}
                                            <span
                                                style={{
                                                    fontWeight: 400,
                                                    opacity: 0.6,
                                                }}
                                            >
                                                (optional)
                                            </span>
                                        </>
                                    }
                                >
                                    <textarea
                                        value={comment}
                                        onChange={(e) =>
                                            setComment(e.target.value)
                                        }
                                        placeholder="Any specific requirements or questions?"
                                        maxLength={2000}
                                        rows={3}
                                        className={inquiryFormTextareaStyle}
                                    />
                                </FormEntry>
                            </div>
                        </div>
                    </div>

                    {(error ?? validationSummary) && (
                        <div className={inquiryFormErrorStyle} role="alert">
                            {error ?? validationSummary}
                        </div>
                    )}

                    <p className={inquiryFormConsentStyle}>
                        {LEAD_CONSENT_TEXT}
                    </p>

                    <div className={inquiryFormActionsStyle}>
                        <Button
                            variant="CTA"
                            type="submit"
                            // Deliberately not gated on completeness. A disabled
                            // button cannot explain itself, and it also stops
                            // the browser from running its own validation, so
                            // an incomplete form gave the user nothing to act
                            // on. Submitting now always produces feedback:
                            // either a native message on the offending field,
                            // or one of findValidationProblems' messages.
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Submitting..." : "Submit inquiry"}
                        </Button>
                        <Button
                            variant="tertiary"
                            onClick={onClose}
                            type="button"
                        >
                            Cancel
                        </Button>
                    </div>
                </form>
            </CustomDialog>
        </>
    );
};

export default PrivateOfficeInquiryForm;
